import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, XCircle, Hourglass } from "@phosphor-icons/react";
import { fmtMoney } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// 2026-02-21 — Total budget ~40s (20 polls × 2s) before falling to
// "Payment processing" recovery state. Never a dead-end.
const MAX_POLLS = 20;
const POLL_INTERVAL_MS = 2000;
const KIOSK_RESET_MS = 30000;

// 2026-02-21 — Clear all known cart storage keys so the next kiosk
// customer starts fresh. Belt-and-braces — uses every key the cart
// has used across modes.
function clearCustomerCartState() {
  try {
    const keys = ["cart_v1", "cart", "kiosk_cart", "qr_cart", "online_cart"];
    keys.forEach((k) => localStorage.removeItem(k));
    sessionStorage.removeItem("cart_v1");
  } catch {
    /* private mode / disabled storage — non-fatal */
  }
}

// iter_AUTH_ISOLATION (2026-02-28) — Customer/staff session isolation.
// The customer-facing PaymentReturn flow MUST NOT allow a token-bearing
// device to slip into the staff shell. Historically the "Done" button
// navigated to "/" (a protected route) — on shared iPads that still had
// a valid staff `mm_token` in localStorage, this dropped the customer
// straight into POS. We now route to a customer-safe destination based
// on `mode` and NEVER navigate into a protected path from this page.
function customerSafeDestination({ mode, venueId }) {
  const q = venueId ? `?venue=${encodeURIComponent(venueId)}` : "";
  if (mode === "kiosk")  return `/order${q}${q ? "&" : "?"}mode=kiosk`;
  if (mode === "qr" && sessionStorage.getItem("mm_qr_path")) return sessionStorage.getItem("mm_qr_path"); // back to the table's AI order
  if (mode === "qr")     return `/order${q}${q ? "&" : "?"}mode=qr`;
  if (mode === "online" && venueId) return `/venue/${venueId}`;
  // Fallback for legacy links: land on the public queue for the venue
  // if we know it, otherwise the login screen (never the app shell).
  if (venueId) return `/queue?venue=${encodeURIComponent(venueId)}`;
  return "/login";
}

export default function PaymentReturn({ outcome }) {
  // outcome: "success" | "cancel"
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const orderIdFromUrl = params.get("order_id");
  const mode = params.get("mode") || ""; // kiosk | online | qr (optional)
  const venueId = params.get("venue") || params.get("venue_id") || "";
  const isKiosk = mode === "kiosk";

  const [status, setStatus] = useState(outcome === "cancel" ? "cancelled" : "checking");
  const [order, setOrder] = useState(null);
  const [polls, setPolls] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(Math.round(KIOSK_RESET_MS / 1000));

  // Normalise an order coming from either /stripe/status (full order doc)
  // or /payments/recover (flat fields).
  const normaliseRecover = (r) => ({
    id: r.order_id,
    ticket_number: r.ticket_number,
    status: r.order_status,
    total: r.total ?? null,
    currency: r.currency || "AUD",
  });

  useEffect(() => {
    if (outcome === "cancel") return;
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let done = false;

    const succeed = (o) => {
      if (done || cancelled) return;
      done = true;
      setOrder(o);
      setStatus("paid");
      clearCustomerCartState();
    };

    const finalRecover = async () => {
      // Last-chance lookup against our DB before giving up.
      try {
        const r = await axios.get(`${API}/payments/recover/${sessionId}`);
        if (r.data?.paid) {
          succeed(normaliseRecover(r.data));
          return true;
        }
        if (r.data?.order_id) {
          // Order exists but webhook hasn't fired — never a dead-end.
          setOrder({ id: r.data.order_id, ticket_number: r.data.ticket_number });
          setStatus("processing");
          return true;
        }
      } catch (e) {
        console.error("final recover failed", e);
      }
      return false;
    };

    const pollStripe = async (n = 0) => {
      if (cancelled || done) return;
      try {
        const r = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
        if (cancelled || done) return;
        setPolls(n + 1);
        if (r.data.payment_status === "paid" && r.data.order) {
          succeed(r.data.order); return;
        }
        if (r.data.status === "expired" || r.data.status === "cancelled") {
          setStatus("cancelled"); done = true; return;
        }
        if (n >= MAX_POLLS) { if (!(await finalRecover())) setStatus("timeout"); return; }
        setTimeout(() => pollStripe(n + 1), POLL_INTERVAL_MS);
      } catch (e) {
        console.error("stripe status poll", e);
        if (n >= MAX_POLLS) { if (!(await finalRecover())) setStatus("error"); }
        else setTimeout(() => pollStripe(n + 1), POLL_INTERVAL_MS + 500);
      }
    };

    const pollRecover = async (n = 0) => {
      // Parallel DB-only poll — wins on webhook-first ordering.
      if (cancelled || done) return;
      try {
        const r = await axios.get(`${API}/payments/recover/${sessionId}`);
        if (cancelled || done) return;
        if (r.data?.paid) { succeed(normaliseRecover(r.data)); return; }
        // 2026-02-21 — Optimisation: if Stripe paid the customer but our
        // webhook hasn't promoted yet AND we've polled enough that this
        // looks persistent (5+ rounds = 10s), surface the "processing"
        // recovery state so the kiosk customer sees a reference instead
        // of staying on the spinner for the full 40s window.
        if (n >= 5 && r.data?.order_id && !done) {
          setOrder({ id: r.data.order_id, ticket_number: r.data.ticket_number });
          // Don't set 'paid' — order isn't promoted. Set 'processing'
          // only if we haven't already succeeded via the other poll.
          setStatus((s) => (s === "checking" ? "processing" : s));
        }
      } catch {
        /* non-fatal — stripe poll is still running */
      }
      if (n >= MAX_POLLS) return;
      setTimeout(() => pollRecover(n + 1), POLL_INTERVAL_MS);
    };

    pollStripe(0);
    pollRecover(0);
    return () => { cancelled = true; };
  }, [sessionId, outcome]);

  // 2026-02-21 — Kiosk auto-reset: after KIOSK_RESET_MS on the success
  // screen, navigate back to the order page so the next customer can
  // start without staff intervention.
  useEffect(() => {
    if (status !== "paid" || !isKiosk) return;
    const startedAt = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, Math.round((KIOSK_RESET_MS - elapsed) / 1000));
      setSecondsLeft(remaining);
      if (elapsed >= KIOSK_RESET_MS) {
        clearInterval(tick);
        const target = venueId
          ? `/order?venue=${encodeURIComponent(venueId)}&mode=kiosk`
          : "/";
        nav(target);
      }
    }, 250);
    return () => clearInterval(tick);
  }, [status, isKiosk, venueId, nav]);

  const shortOrderRef = useMemo(() => {
    const id = order?.id || orderIdFromUrl || "";
    return id ? id.slice(-6).toUpperCase() : "";
  }, [order, orderIdFromUrl]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--sand-50)] p-6"
         data-testid="payment-return-page">
      <div className="max-w-md w-full text-center fade-up">
        {status === "checking" && (
          <>
            <div className="w-16 h-16 rounded-full border-4 border-[var(--sand-100)] border-t-[var(--terracotta)] animate-spin mx-auto mb-5" />
            <div className="label-eyebrow mb-2">Verifying payment</div>
            <h1 className="font-heading text-2xl font-semibold">Almost there…</h1>
            <p className="text-sm text-[var(--ink-500)] mt-2 tabular">attempt {polls + 1} / {MAX_POLLS}</p>
          </>
        )}
        {status === "paid" && order && (
          <>
            <div className="w-20 h-20 rounded-full bg-[var(--moss-bg)] flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={40} weight="fill" className="text-[var(--moss)]" />
            </div>
            <div className="label-eyebrow mb-2">Payment received</div>
            <div className="font-heading text-5xl font-semibold tabular text-[var(--terracotta)]"
                 data-testid="pay-success-ticket">#{order.ticket_number}</div>
            <p className="text-[var(--ink-700)] mt-3"
               data-testid="pay-success-message">Your order has been sent to the kitchen.</p>
            {order.total != null && (
              <p className="text-xs text-[var(--ink-500)] mt-1 tabular">
                Paid · {fmtMoney(order.total, order.currency || "AUD")}
              </p>
            )}
            {isKiosk ? (
              <p className="text-xs text-[var(--ink-500)] mt-6 tabular"
                 data-testid="kiosk-autoreset-countdown">
                Ready for the next customer in {secondsLeft}s
              </p>
            ) : (
              <button data-testid="pay-success-done"
                      onClick={() => nav(customerSafeDestination({ mode, venueId }))}
                      className="mt-8 h-12 px-8 rounded-md bg-[var(--ink-900)] text-[var(--sand-50)] font-medium active:scale-[0.98]">
                Done
              </button>
            )}
          </>
        )}
        {status === "processing" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[var(--sand-100)] flex items-center justify-center mx-auto mb-5">
              <Hourglass size={40} weight="fill" className="text-[var(--ink-700)]" />
            </div>
            <div className="label-eyebrow mb-2">Payment processing</div>
            <h1 className="font-heading text-2xl font-semibold"
                data-testid="pay-processing-heading">We've received your payment</h1>
            <p className="text-sm text-[var(--ink-500)] mt-3">
              Your ticket is being prepared. If it doesn't update in a few moments,
              please show this screen to a staff member at the counter.
            </p>
            {shortOrderRef && (
              <p className="text-xs text-[var(--ink-500)] mt-4 tabular"
                 data-testid="pay-processing-ref">Reference: order #{shortOrderRef}</p>
            )}
          </>
        )}
        {status === "timeout" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[var(--sand-100)] flex items-center justify-center mx-auto mb-5">
              <Hourglass size={40} weight="fill" className="text-[var(--ink-700)]" />
            </div>
            <div className="label-eyebrow mb-2">Still confirming</div>
            <h1 className="font-heading text-2xl font-semibold">Payment processing</h1>
            <p className="text-sm text-[var(--ink-500)] mt-3"
               data-testid="pay-timeout-message">
              Stripe is taking a bit longer than usual. If you've been charged,
              please show this screen to a staff member at the counter — your
              order is safe.
            </p>
            {shortOrderRef && (
              <p className="text-xs text-[var(--ink-500)] mt-4 tabular">
                Reference: order #{shortOrderRef}
              </p>
            )}
          </>
        )}
        {(status === "cancelled" || status === "error") && (
          <>
            <div className="w-20 h-20 rounded-full bg-[var(--destructive-bg)] flex items-center justify-center mx-auto mb-5">
              <XCircle size={40} weight="fill" className="text-[var(--destructive)]" />
            </div>
            <div className="label-eyebrow mb-2">{status === "cancelled" ? "Cancelled" : "Something went wrong"}</div>
            <h1 className="font-heading text-2xl font-semibold">Payment was not completed</h1>
            <p className="text-sm text-[var(--ink-500)] mt-3">No charge was made. You can try again from where you left off.</p>
            <button data-testid="pay-back"
                    onClick={() => { toast.info("Returning"); window.history.length > 1 ? window.history.back() : nav(customerSafeDestination({ mode, venueId })); }}
                    className="mt-8 h-12 px-8 rounded-md bg-[var(--ink-900)] text-[var(--sand-50)] font-medium active:scale-[0.98]">
              Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}
